import { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { setSeo } from "@/lib/seo";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setSeo({ title: "Admin — Serrurier Lagny sur Marne", noindex: true });
  }, []);

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-brand-mist px-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-10 justify-center">
          <span className="grid place-items-center w-10 h-10 rounded-lg bg-brand-navy text-white font-display font-bold">
            S
          </span>
          <span className="font-display font-semibold tracking-tight text-brand-navy">
            SERRURIER LAGNY · ADMIN
          </span>
        </div>
        <form
          onSubmit={submit}
          data-testid="admin-login-form"
          className="rounded-2xl border border-brand-line bg-white p-8 md:p-10 shadow-sm"
        >
          <div className="flex items-center gap-2 text-brand-navy mb-6">
            <Lock className="w-5 h-5 text-brand-gold" />
            <h1 className="font-display font-semibold text-2xl tracking-tight">
              Connexion
            </h1>
          </div>

          {error && (
            <div
              data-testid="login-error"
              className="mb-5 text-sm text-red-600 border border-red-200 bg-red-50 px-4 py-3 rounded-lg"
            >
              {error}
            </div>
          )}

          <label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-medium">
            Email
          </label>
          <input
            data-testid="admin-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-lg border border-brand-line px-4 py-3 mt-1 mb-6 text-brand-navy focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/10 focus:outline-none transition-colors duration-200"
            placeholder="admin@serrurier-lagny.fr"
          />
          <label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-medium">
            Mot de passe
          </label>
          <input
            data-testid="admin-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-lg border border-brand-line px-4 py-3 mt-1 mb-8 text-brand-navy focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/10 focus:outline-none transition-colors duration-200"
            placeholder="••••••••"
          />
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-navy2 disabled:opacity-60 text-white px-6 py-4 rounded-full font-semibold transition-colors duration-300"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Se connecter
          </button>
        </form>
      </div>
    </div>
  );
}
